import React from 'react'
import { matchPath, Route, Switch, useLocation } from 'react-router'

import { parse } from 'query-string'

import {
  CWV_ROUTES,
  getAllTemplatesForVersionedRoute,
  getVersionRenderStatus,
  routes,
} from '@sketch/modules-common'
import { DocumentHead, LoadingState } from '@sketch/components'

import { useVersioning } from '../../../versioning'
import { useGetVersionQueryWithPolling } from '../../operations'
import { LoadingMessage } from '../../components/LoadingMessage'

import DocumentView from '../../DocumentView'
import {
  DocumentSidebarLayout,
  DocumentSidebarLayoutExtraProps,
} from '../../components/DocumentSidebarLayout'
import {
  PublicWorkspaceFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import {
  ArtboardDetailQueries,
  ArtboardDetailQueriesProps,
} from '../../FrameView/components/ArtboardDetailQueries'
import { ShareCanvasRoute } from '../ShareCanvasRoute'
import { UnsubscribeShareNotifications } from '../../components/UnsubscribeShareNotifications'

/**
 * The bare share URL is the only canvas route you can reach before the
 * document has finished processing, so it is the only one that waits on the
 * presentation file.
 */
const isShareViewRoute = (path: string) =>
  getAllTemplatesForVersionedRoute('SHARE_VIEW').includes(path)

/**
 * The routes that must render before the presentation file is ready.
 *
 * The bare share URL is where you land from a link while the document is still
 * processing, and unsubscribe arrives from an email and has to take effect
 * whatever the document is doing. `ShareMainRoutes` says the same thing to
 * `ShareVersionProvider` through `skipPresentationFileCheck`; these two used to
 * be handled there, and the gate below would otherwise hold them back now that
 * they are handled here.
 */
const RENDERS_BEFORE_PRESENTATION_FILE = [
  ...getAllTemplatesForVersionedRoute('SHARE_VIEW'),
  routes.SHARE_UNSUBSCRIBE.template(),
]

const DocumentSidebarLayoutRoutesSwitch = (
  layoutProps: DocumentSidebarLayoutExtraProps
) => {
  const versioning = useVersioning()
  const location = useLocation()

  const { data } = useGetVersionQueryWithPolling({
    variables: {
      shareIdentifier: versioning.share.identifier,
      versionShortId: versioning.versionShortId,
    },
  })

  const renderStatus = getVersionRenderStatus(data?.share?.version)

  const presentationStatus =
    data?.share?.version?.presentationStatus ?? 'PENDING'
  const isProcessingPresentationFile = presentationStatus === 'PENDING'

  const waitsForPresentationFile = !matchPath(location.pathname, {
    path: RENDERS_BEFORE_PRESENTATION_FILE,
    exact: true,
  })

  if (isProcessingPresentationFile && waitsForPresentationFile) {
    // this will show the header + the loading state, so the user can go back
    return (
      <LoadingState
        data-testid="loader-docsidebarlayout"
        type="color-icon"
        size="48px"
        message={<LoadingMessage />}
      />
    )
  }

  return (
    <Switch>
      {/* Artboard detail view */}
      <Route
        exact
        path={[...getAllTemplatesForVersionedRoute('ARTBOARD_DETAIL')]}
        render={routeProps => (
          <ArtboardDetailQueries
            {...(routeProps as ArtboardDetailQueriesProps)}
          />
        )}
      />
      {/*
        The canvas views - the bare share URL, page, Section and frame - share
        one route so that moving between them does not unmount the canvas and
        refetch the file. One Route element, so React keeps the subtree: a
        second Route rendering the same component would still remount it.
        Which view renders is decided inside, from the same route the canvas
        scope reads.
      */}
      <Route
        exact
        path={[
          ...getAllTemplatesForVersionedRoute('SHARE_VIEW'),
          routes.SHARE_UNSUBSCRIBE.template(),
          ...getAllTemplatesForVersionedRoute('FRAME'),
          ...getAllTemplatesForVersionedRoute('SHARE_PAGE_CANVAS_VIEW'),
          ...getAllTemplatesForVersionedRoute('PAGE_SECTION'),
        ]}
        render={routeProps => {
          const { search } = parse(routeProps.location.search)

          return (
            <>
              {search ? (
                // Searching shows the results grid, which is not a canvas.
                <>
                  <DocumentHead title={`${versioning.share.name} results`} />
                  <DocumentView {...layoutProps} {...routeProps} />
                </>
              ) : (
                <ShareCanvasRoute
                  {...layoutProps}
                  {...routeProps}
                  // Read here rather than in the canvas route, so that the
                  // query behind it keeps the two consumers it has: this
                  // switch and the view being shown.
                  renderStatus={renderStatus}
                  // The bare share URL is the only canvas route you can reach
                  // before the presentation file is ready, so it is the only
                  // one that can be waiting on it.
                  isWaitingForPresentationFile={
                    isProcessingPresentationFile &&
                    isShareViewRoute(routeProps.match.path)
                  }
                />
              )}
              {/*
                Mounted as a sibling of the view rather than instead of it, so
                the share still loads and shows while the unsubscribe happens.
                Keeping it in the router rather than in a view means changing
                the view cannot silently break unsubscribing.
              */}
              <Route
                exact
                path={routes.SHARE_UNSUBSCRIBE.template()}
                component={UnsubscribeShareNotifications}
              />
            </>
          )
        }}
      />

      {/*
        - Legacy Page view (SHARE_PAGE_VIEW)
        - Prototype view (SHARE_PROTOTYPES)
        - CWV view (symbol, text styles, layer styles or color variables)
        */}
      <Route
        exact
        path={[
          ...getAllTemplatesForVersionedRoute('SHARE_PAGE_VIEW'),
          ...getAllTemplatesForVersionedRoute('SHARE_PROTOTYPES'),
          ...CWV_ROUTES,
        ]}
        render={routeProps => <DocumentView {...layoutProps} {...routeProps} />}
      />
    </Switch>
  )
}

export const DocumentSidebarLayoutRoutes = ({
  workspace,
}: {
  workspace: WorkspaceMinimalFragment | PublicWorkspaceFragment
}) => (
  <DocumentSidebarLayout
    workspace={workspace}
    render={layoutProps => (
      <DocumentSidebarLayoutRoutesSwitch {...layoutProps} />
    )}
  />
)
